import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import routes from "constants/routes";
import { COUPON_STATUS, ZERO } from "constants/coupons";
import {
  formatCouponCategory,
  formatCouponDate,
  formatCouponDateRange,
  formatUsageLimit,
  getCouponStatusLabel,
  resolveCouponStatus,
} from "utils/couponUtils";
import { resolveCouponCodeFromApi } from "helpers/couponApi";
import { formatApiErrorForDisplay } from "helpers/packageManagementApi";
import {
  useActiveCouponsList,
  useCouponLookup,
} from "store/backend/apiHooks";
import {
  Icon,
  Loader,
  QueryErrorPanel,
  SearchBar,
} from "widgets";
import { useSnackbarContext } from "widgets/SnackBar/SnackBar";

import styles from "./CouponsList.module.scss";

const STATUS_CLASS_MAP = {
  [COUPON_STATUS.ACTIVE]: styles.statusActive,
  [COUPON_STATUS.INACTIVE]: styles.statusInactive,
  [COUPON_STATUS.EXPIRED]: styles.statusExpired,
  [COUPON_STATUS.SCHEDULED]: styles.statusScheduled,
};

const getSearchErrorMessage = (error) =>
  formatApiErrorForDisplay(error, "Failed to fetch coupon details.");

const CouponsList = () => {
  const navigate = useNavigate();
  const { openSnackbar } = useSnackbarContext();
  const [couponCodeInput, setCouponCodeInput] = useState("");
  const { mutateAsync: lookupCoupon, isPending: isLookingUpCoupon } =
    useCouponLookup();

  const {
    data: activeCouponsData,
    isLoading,
    isError,
    error,
    refetch,
  } = useActiveCouponsList();

  const recentCoupons = activeCouponsData?.coupons ?? [];

  const handleCouponLookup = async (couponCode) => {
    try {
      const lookedUpCoupon = await lookupCoupon(couponCode);
      const resolvedCode =
        resolveCouponCodeFromApi(lookedUpCoupon) || couponCode;
      navigate(routes.coupons.view(resolvedCode));
    } catch (lookupError) {
      openSnackbar(getSearchErrorMessage(lookupError), "error");
    }
  };

  const handleCouponCodeKeyDown = (event) => {
    if (event.key !== "Enter") {
      return;
    }

    const couponCode = couponCodeInput.trim();
    if (couponCode.length === ZERO) {
      return;
    }

    event.preventDefault();
    handleCouponLookup(couponCode);
  };

  return (
    <div className={styles.container}>
      <div className={styles.filterCard}>
        <div className={styles.filterRow}>
          <div className={styles.searchFilter}>
            <SearchBar
              value={couponCodeInput}
              onChange={setCouponCodeInput}
              onKeyDown={handleCouponCodeKeyDown}
              placeholder="Enter coupon code and press Enter"
              disabled={isLookingUpCoupon}
            />
          </div>

          <div className={styles.filterActions}>
            <button
              type="button"
              className={styles.createButton}
              onClick={() => navigate(routes.coupons.create)}
            >
              <Icon name="add" fontSize="small" />
              Create Coupon
            </button>
          </div>
        </div>
      </div>

      <div className={styles.tableCard}>
        <h2 className={styles.recentCouponsHeading}>Recent Coupons</h2>

        {isLoading ? (
          <div className={styles.emptyState}>
            <Loader />
          </div>
        ) : isError ? (
          <div className={styles.errorState} role="alert">
            <QueryErrorPanel
              message={formatApiErrorForDisplay(
                error,
                "Failed to load active coupons."
              )}
              onRetry={refetch}
            />
          </div>
        ) : (
          <div className={styles.tableScroll}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Coupon Code</th>
                  <th>Coupon Category</th>
                  <th>Discount Type</th>
                  <th>Validity</th>
                  <th>Usage</th>
                  <th>Status</th>
                  <th>Created On</th>
                </tr>
              </thead>
              <tbody>
                {recentCoupons.length === ZERO ? (
                  <tr>
                    <td colSpan={7} className={styles.emptyState}>
                      No active coupons found.
                    </td>
                  </tr>
                ) : (
                  recentCoupons.map((coupon) => {
                    const couponStatus = resolveCouponStatus(
                      coupon.effectiveDate,
                      coupon.expiryDate
                    );

                    return (
                      <tr
                        key={coupon.id}
                        className={styles.clickableRow}
                        onClick={() => navigate(routes.coupons.view(coupon.id))}
                      >
                        <td className={styles.codeCell} title={coupon.code}>
                          {coupon.code}
                        </td>
                        <td>{formatCouponCategory(coupon.category)}</td>
                        <td style={{ textTransform: "capitalize" }}>
                          {coupon.discountType}
                        </td>
                        <td>
                          {formatCouponDateRange(
                            coupon.effectiveDate,
                            coupon.expiryDate
                          )}
                        </td>
                        <td>{formatUsageLimit(coupon)}</td>
                        <td>
                          <span
                            className={`${styles.statusBadge} ${STATUS_CLASS_MAP[couponStatus]}`}
                          >
                            {getCouponStatusLabel(couponStatus)}
                          </span>
                        </td>
                        <td>{formatCouponDate(coupon.createdOn)}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default CouponsList;
